import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useProducts } from "../../products/hooks/useProducts";
import { useBrands } from "../../products/hooks/useBrands";
import { useCategories } from "../../products/hooks/useCategories";
import { useProductPricing } from "./useProductPricing";
import { useCreateSale } from "../../sales/hooks/useCreateSale";
import { api, ApiError } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";
import { getDefaultScannerMode } from "../barcode/device";
import { useBarcodeScanner } from "../barcode/useBarcodeScanner";
import type { ExternalBarcodeData } from "../barcode/useBarcodeScanner";
import type { DealerOption } from "../barcode/QuickAddProductModal";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { trackEvent } from "../../../lib/tracking";
import { trackFirstSaleCompletedOnce } from "../../../lib/trackingMilestones";
import { cartHasNonCompliantGst, computePosGstLine } from "../gst/posGstMath";
import type { PosCartItem } from "../types";
import { getProductGstConfig } from "../types";

export type PosQuote = {
  applied_promotion: null | { id: string; name: string; code: string | null; type: string; priority: number };
  totals: { subtotal: string; discount: string; taxable: string; gst: string; grand_total: string };
};

export type CardPricingRow = {
  unit_price: number;
  mrp: number | null;
  available_qty: number;
};

export function digitsOnly(s: string): string {
  return s.replace(/\D/g, "");
}

export type UsePosSessionKeyboardOptions = {
  searchInputRef?: RefObject<HTMLInputElement | null>;
  onCloseMobileCart?: () => void;
};

export function usePosSession(keyboard?: UsePosSessionKeyboardOptions | null) {
  const keyboardRef = useRef(keyboard);
  keyboardRef.current = keyboard;
  const { token } = useAuth();
  const { products, loading: productsLoading, error: productsError, reload: reloadProducts } = useProducts();
  const { brands } = useBrands();
  const { categories } = useCategories();
  const { getPricing, prefetchPricing, clearCache } = useProductPricing();
  const { createSale, loading: checkoutLoading } = useCreateSale();

  const [cart, setCart] = useState<PosCartItem[]>([]);
  const [expandedCartPid, setExpandedCartPid] = useState<string | null>(null);
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "razorpay" | "gpay" | "upi">("cash");
  const lookupTimerRef = useRef<number | null>(null);
  const [query, setQuery] = useState("");
  const [productView, setProductView] = useState<"grid" | "list">("grid");
  const [cartHydrated, setCartHydrated] = useState(false);
  const [scannerMode] = useState(() => getDefaultScannerMode());
  const [cameraOpen, setCameraOpen] = useState(false);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanModalMode, setScanModalMode] = useState<"external" | "manual">("manual");
  const [scanBarcode, setScanBarcode] = useState("");
  const [scanExternalPrefill, setScanExternalPrefill] = useState<ExternalBarcodeData | null>(null);
  const [dealers, setDealers] = useState<DealerOption[]>([]);
  const saveTimerRef = useRef<number | null>(null);
  const hydrateInFlightRef = useRef(false);
  const [cardPricingByProductId, setCardPricingByProductId] = useState<Record<string, CardPricingRow>>({});

  const [quote, setQuote] = useState<null | PosQuote>(null);
  const quoteTimerRef = useRef<number | null>(null);

  const formatQty = useCallback((n: number) => {
    if (!Number.isFinite(n)) return "0";
    const str = n.toFixed(3);
    return str.replace(/\.?0+$/, "");
  }, []);

  const filteredProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const activeOnly = products.filter((p) => {
      const s = (p.status ?? "").trim().toLowerCase();
      const isInactive = s === "inactive" || s === "disabled" || s === "off";
      return !isInactive;
    });
    if (!q) return activeOnly;
    return activeOnly.filter((p) => {
      const name = p.name.toLowerCase();
      const sku = p.sku?.toLowerCase() ?? "";
      return name.includes(q) || sku.includes(q);
    });
  }, [products, query]);

  useEffect(() => {
    if (!token) return;
    if (productsLoading) return;
    if (filteredProducts.length === 0) return;

    const visible = filteredProducts.slice(0, 36).map((p) => String(p.id));
    void (async () => {
      try {
        const rows = await prefetchPricing(visible);
        if (Object.keys(rows).length === 0) return;
        setCardPricingByProductId((prev) => {
          const next = { ...prev };
          for (const [pid, pr] of Object.entries(rows)) {
            next[pid] = { unit_price: pr.unit_price, mrp: pr.mrp, available_qty: pr.available_qty };
          }
          return next;
        });
      } catch {
        // Ignore; cards still render, and add-to-cart will surface issues.
      }
    })();
  }, [filteredProducts, prefetchPricing, productsLoading, token]);

  useEffect(() => {
    if (!token) {
      setDealers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.dealers.list({ token });
        if (!cancelled) {
          setDealers(
            res.data.map((d) => ({
              id: d.id,
              name: d.name,
              is_default: d.is_default,
              is_system_generated: d.is_system_generated,
            })),
          );
        }
      } catch {
        if (!cancelled) setDealers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const cartLines = useMemo(() => {
    return cart.map((item) => {
      const product = products.find((p) => String(p.id) === item.product_id);
      const cfg = getProductGstConfig(product);
      const fifo = item.unit_price * item.quantity;
      const gst = computePosGstLine({
        fifoLineTotal: fifo,
        isGstApplicable: cfg.isGstApplicable,
        gstType: cfg.gstType,
        productGstRate: cfg.gstRatePercent,
        gst_override_type: item.gst_override_type,
        gst_override_value: item.gst_override_value,
      });

      return { item, product, gst };
    });
  }, [cart, products]);

  const totals = useMemo(() => {
    return cartLines.reduce(
      (acc, row) => ({
        taxable: acc.taxable + row.gst.taxable,
        gst: acc.gst + row.gst.gst,
        grand: acc.grand + row.gst.total,
      }),
      { taxable: 0, gst: 0, grand: 0 },
    );
  }, [cartLines]);

  useEffect(() => {
    if (!token) {
      setQuote(null);
      return;
    }
    if (!cartHydrated) return;
    if (cart.length === 0) {
      setQuote(null);
      return;
    }

    if (quoteTimerRef.current !== null) {
      window.clearTimeout(quoteTimerRef.current);
    }
    quoteTimerRef.current = window.setTimeout(() => {
      void api.posCart
        .quote({
          token,
          payload: {
            items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
            promo_code: promoCode.trim() ? promoCode.trim() : null,
          },
        })
        .then((res) => {
          setQuote(res.data);
        })
        .catch(() => {
          setQuote(null);
        });
    }, 250);

    return () => {
      if (quoteTimerRef.current !== null) {
        window.clearTimeout(quoteTimerRef.current);
        quoteTimerRef.current = null;
      }
    };
  }, [cart, cartHydrated, promoCode, token]);

  const showComplianceWarning = useMemo(() => cartHasNonCompliantGst(cartLines), [cartLines]);

  useEffect(() => {
    if (!token) {
      setCart([]);
      setCartHydrated(false);
      return;
    }
    if (productsLoading) return;
    if (cartHydrated) return;
    if (hydrateInFlightRef.current) return;

    let cancelled = false;
    hydrateInFlightRef.current = true;
    (async () => {
      try {
        const res = await api.posCart.current({ token });
        if (cancelled) return;
        if (!res.cart || !Array.isArray(res.cart.items) || res.cart.items.length === 0) {
          setCartHydrated(true);
          return;
        }

        const idsToPrefetch = Array.from(new Set(res.cart.items.map((it) => String(it.product_id))));
        try {
          const rows = await prefetchPricing(idsToPrefetch);
          if (!cancelled && Object.keys(rows).length > 0) {
            setCardPricingByProductId((prev) => {
              const next = { ...prev };
              for (const [pid, pr] of Object.entries(rows)) {
                next[pid] = { unit_price: pr.unit_price, mrp: pr.mrp, available_qty: pr.available_qty };
              }
              return next;
            });
          }
        } catch {
          // continue; per-item fallback below
        }

        const next: PosCartItem[] = [];
        for (const it of res.cart.items) {
          const productId = String(it.product_id);
          const product = products.find((p) => String(p.id) === productId);
          const name = product?.name ?? `Product ${productId}`;

          const desiredQty = Number.parseFloat(String(it.quantity));
          if (!Number.isFinite(desiredQty) || desiredQty <= 0) continue;

          let unit_price = 0;
          let mrp: number | null = null;
          let available_qty = 0;
          try {
            const pricing = await getPricing(productId);
            unit_price = pricing.unit_price;
            mrp = pricing.mrp;
            available_qty = pricing.available_qty;
          } catch {
            available_qty = Number.POSITIVE_INFINITY;
          }

          const safeQty = Math.max(0, Math.min(desiredQty, available_qty));
          if (safeQty <= 0) continue;

          const ot = it.gst_override_type;
          next.push({
            product_id: productId,
            name,
            quantity: safeQty,
            unit_price,
            mrp,
            available_qty,
            gst_override_type: ot === "manual_rate" || ot === "no_tax" ? ot : null,
            gst_override_value:
              it.gst_override_value != null && it.gst_override_value !== ""
                ? Number.parseFloat(String(it.gst_override_value))
                : null,
            gst_override_reason: typeof it.gst_override_reason === "string" ? it.gst_override_reason : "",
          });
        }

        setCart(next);
        setCartHydrated(true);
      } catch (e) {
        setCartHydrated(true);
        toast.error(e instanceof Error ? e.message : "Failed to load initiated cart");
      } finally {
        hydrateInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      hydrateInFlightRef.current = false;
    };
  }, [cartHydrated, getPricing, prefetchPricing, products, productsLoading, token]);

  useEffect(() => {
    if (!token) return;
    if (!cartHydrated) return;

    if (cart.length === 0) return;

    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      void api.posCart
        .updateCurrent({
          token,
          payload: {
            items: cart.map((i) => ({
              product_id: i.product_id,
              quantity: i.quantity,
              gst_override_type: i.gst_override_type ?? null,
              gst_override_value: i.gst_override_value ?? null,
              gst_override_reason: i.gst_override_reason?.trim() ? i.gst_override_reason : null,
            })),
          },
        })
        .catch(() => {
          // Silent: cart remains usable even if persistence fails momentarily.
        });
    }, 300);

    return () => {
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [cart, cartHydrated, token]);

  useEffect(() => {
    if (!token) return;
    const d = digitsOnly(customerPhone);
    if (d.length !== 10) return;

    if (lookupTimerRef.current !== null) {
      window.clearTimeout(lookupTimerRef.current);
    }
    lookupTimerRef.current = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await api.customers.lookup({ token, phone: customerPhone });
          const c = res.data?.customer;
          if (c?.name) setCustomerName(c.name);
        } catch {
          // ignore lookup errors
        }
      })();
    }, 400);

    return () => {
      if (lookupTimerRef.current !== null) {
        window.clearTimeout(lookupTimerRef.current);
        lookupTimerRef.current = null;
      }
    };
  }, [token, customerPhone]);

  async function addProductToCart(productId: string, name: string): Promise<boolean> {
    try {
      const pricing = await getPricing(productId);
      let updated = false;
      setCart((prev) => {
        const existing = prev.find((i) => i.product_id === productId);
        if (!existing) {
          const qty = pricing.available_qty > 0 ? 1 : 0;
          if (qty <= 0) {
            toast.error("This product is out of stock");
            return prev;
          }
          updated = true;
          return [
            ...prev,
            {
              product_id: productId,
              name,
              quantity: qty,
              unit_price: pricing.unit_price,
              mrp: pricing.mrp,
              available_qty: pricing.available_qty,
              gst_override_type: null,
              gst_override_value: null,
              gst_override_reason: "",
            },
          ];
        }

        const nextQty = Math.min(existing.quantity + 1, existing.available_qty);
        if (nextQty === existing.quantity) {
          toast.error(`Only ${existing.available_qty} left for this product`);
          return prev;
        }
        updated = true;
        return prev.map((i) => (i.product_id === productId ? { ...i, quantity: nextQty } : i));
      });
      if (updated) {
        trackEvent(TRACKING_EVENTS.CART_ITEM_ADDED);
      }
      return updated;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to fetch pricing");
      return false;
    }
  }

  const { resolveBarcode, resolving } = useBarcodeScanner({
    onLocalProduct: async ({ id, name }) => {
      const ok = await addProductToCart(id, name);
      if (ok) toast.success("Product added to cart");
    },
    onExternalProduct: async (data) => {
      setScanBarcode(data.barcode);
      setScanExternalPrefill(data);
      setScanModalMode("external");
      setScanModalOpen(true);
    },
    onNotFoundExternal: async (barcode) => {
      setScanBarcode(barcode);
      setScanExternalPrefill(null);
      setScanModalMode("manual");
      setScanModalOpen(true);
      toast.message("No product found, please add manually");
    },
  });

  function setItemQty(productId: string, nextQty: number) {
    let removed = false;
    setCart((prev) => {
      const cur = prev.find((i) => i.product_id === productId);
      if (cur) {
        const clamped = Math.max(0, Math.min(nextQty, cur.available_qty));
        if (clamped < cur.quantity) removed = true;
      }
      return prev
        .map((i) => {
          if (i.product_id !== productId) return i;
          const clamped = Math.max(0, Math.min(nextQty, i.available_qty));
          return { ...i, quantity: clamped };
        })
        .filter((i) => i.quantity > 0);
    });
    if (removed) {
      trackEvent(TRACKING_EVENTS.CART_ITEM_REMOVED);
    }
  }

  async function onCheckout() {
    if (checkoutLoading) return;
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    const nameTrim = customerName.trim();
    if (!nameTrim) {
      toast.error("Enter customer name");
      return;
    }
    if (digitsOnly(customerPhone).length !== 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    try {
      for (const row of cartLines) {
        const t = row.item.gst_override_type;
        if (t === "manual_rate" || t === "no_tax") {
          if (!row.item.gst_override_reason?.trim()) {
            toast.error("GST override requires a reason.");
            return;
          }
          if (t === "manual_rate" && (row.item.gst_override_value == null || !Number.isFinite(row.item.gst_override_value))) {
            toast.error("Enter a custom GST rate.");
            return;
          }
        }
      }

      const totalItems = cart.reduce((n, i) => n + i.quantity, 0);
      await createSale({
        items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer_name: nameTrim,
        customer_phone: customerPhone,
        payment_method: paymentMethod,
        promo_code: promoCode.trim() ? promoCode.trim() : null,
      });
      trackEvent(TRACKING_EVENTS.POS_CHECKOUT, {
        amount: totals.grand,
        items: totalItems,
        payment_method: paymentMethod,
      });
      trackFirstSaleCompletedOnce();
      setCart([]);
      setCustomerPhone("");
      setCustomerName("");
      setPromoCode("");
      setPaymentMethod("cash");
      clearCache();
      if (token) {
        void api.posCart.clear({ token });
      }
      toast.success("Sale completed");
    } catch (e) {
      trackEvent(TRACKING_EVENTS.SALE_FAILED, {
        code: e instanceof ApiError ? e.code : "unknown",
      });
      if (e instanceof ApiError && e.code === "INSUFFICIENT_STOCK") {
        const raw = (e.data as { raw?: { product_id?: number | string; available_quantity?: string | number } } | undefined)?.raw;
        const pid = raw?.product_id !== undefined ? String(raw.product_id) : null;
        const available = raw?.available_quantity !== undefined ? Number(raw.available_quantity) : Number.NaN;

        if (pid && Number.isFinite(available)) {
          toast.error(`Only ${available} left for this product`);
          setCart((prev) => {
            const item = prev.find((i) => i.product_id === pid);
            if (!item) return prev;
            if (available <= 0) return prev.filter((i) => i.product_id !== pid);
            return prev.map((i) => (i.product_id === pid ? { ...i, quantity: Math.min(i.quantity, available) } : i));
          });
          return;
        }
      }
      toast.error(e instanceof Error ? e.message : "Checkout failed");
    }
  }

  function newSale() {
    setCart([]);
    setExpandedCartPid(null);
    setCustomerPhone("");
    setCustomerName("");
    setPromoCode("");
    setPaymentMethod("cash");
    setQuote(null);
    setQuery("");
    clearCache();
    if (token) {
      void api.posCart.clear({ token });
    }
  }

  function onPaymentMethodChange(m: "cash" | "razorpay" | "gpay" | "upi") {
    setPaymentMethod(m);
    trackEvent(TRACKING_EVENTS.PAYMENT_METHOD_SELECTED, { method: m });
  }

  const onCheckoutRef = useRef(onCheckout);
  onCheckoutRef.current = onCheckout;
  const newSaleRef = useRef(newSale);
  newSaleRef.current = newSale;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const kb = keyboardRef.current;
      if (!kb) return;

      const target = e.target;
      const inField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable);

      if (e.key === "Escape") {
        if (inField) return;
        kb.onCloseMobileCart?.();
        return;
      }

      if (inField) return;

      if (e.key === "F2") {
        e.preventDefault();
        void onCheckoutRef.current();
        return;
      }
      if (e.key === "F4") {
        e.preventDefault();
        newSaleRef.current();
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        const el = kb.searchInputRef?.current;
        el?.focus();
        el?.select();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return {
    token,
    products,
    productsLoading,
    productsError,
    reloadProducts,
    brands,
    categories,
    dealers,
    getPricing,
    prefetchPricing,
    clearCache,
    checkoutLoading,
    cart,
    setCart,
    expandedCartPid,
    setExpandedCartPid,
    customerPhone,
    setCustomerPhone,
    customerName,
    setCustomerName,
    promoCode,
    setPromoCode,
    paymentMethod,
    setPaymentMethod,
    onPaymentMethodChange,
    query,
    setQuery,
    productView,
    setProductView,
    cartHydrated,
    scannerMode,
    cameraOpen,
    setCameraOpen,
    scanModalOpen,
    setScanModalOpen,
    scanModalMode,
    scanBarcode,
    scanExternalPrefill,
    cardPricingByProductId,
    quote,
    formatQty,
    filteredProducts,
    cartLines,
    totals,
    showComplianceWarning,
    addProductToCart,
    setItemQty,
    onCheckout,
    resolveBarcode,
    resolving,
    newSale,
  };
}
